// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { Col, config, Container, Hidden, Row } from 'react-awesome-styled-grid'
import type { AppStateType } from '@reducers/appstate'
import type { Dispatch } from 'redux'
import { openSubscribeConfirmed } from '../../../actions/navigationActions'
import { withRouter } from 'next/router'
import { connect } from 'react-redux'
import { GoodSansB1, GoodSansB2, GoodSansB3, H3, P, QueensH3 } from '@components/shared/typography'
import { GlobalStyle } from '@components/shared'
import { DownArrow } from '@components/shared/icons/DownArrow'
import { ValueProp } from '@components/ValueProp'
import StoreAvailability from '@components/Availability'
import HtmlHead from '@components/HtmlHead'
import SimpleNav from '@components/SimpleNav'
import { TopBanner } from '@components/shared/TopBanner'
import ChoosePlan from '@components/subscribe/ChoosePlan'
import PromoCode from '@components/PromoCode'
import AuthForm from '@components/auth/AuthForm'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { envConfig } from '../../../env'
import BillingInformation from '@components/subscribe/BillingInformation'
import BackgroundGradient from '@components/BackgroundGradient'
import { Confirmation } from '@components/subscribe/Confirmation'

type PropsType = {
  loggedIn: boolean,
  isSubscribed: boolean,
  isMonthly: boolean,
  headerArray: string[],
  isTrial: boolean,
  welcomeBannerText?: string,
  bannerArray?: string[],
  featureAnnualOnly: boolean,
  subscription_expires_at?: number,
  isPrideBanner: boolean,
  isInLine?: boolean
}

type DipatchPropsType = {
  openSubscribeConfirmed: () => void
}

const stripeKey = envConfig().STRIPE_KEY
const stripePromise = stripeKey ? loadStripe(stripeKey) : null

export function SubscribeFlow1 (props: PropsType & DipatchPropsType): React.Node {
  const [showInLineConfirm, setShowInLineConfirm] = React.useState<boolean>(false)

  if (typeof document !== 'undefined') {
    const body = document.querySelector('body')
    if (body && body.style && body.style.overflow === 'hidden') {
      body.style.overflow = 'auto'
    }
  }
  React.useEffect((): void => {
    if (props.isSubscribed && !props.subscription_expires_at) {
      if (props.isInLine) {
        setShowInLineConfirm(true)
      } else {
        props.openSubscribeConfirmed()
      }
    }
  }, [props.isSubscribed, props.subscription_expires_at])

  if (showInLineConfirm) {
    return <Confirmation />
  }

  let headerText = ''
  if (props.headerArray) {
    props.headerArray.forEach((text: string, index: number): void => {
      const string = text.trim()
      if (!string.length) {
        return
      }
      if (index === 1) {
        headerText = headerText + `<span>${string}</span><br/>`
      } else {
        headerText = headerText + string + `<br/>`
      }
    })
  }

  let bannerText = ''

  if (props.bannerArray) {
    props.bannerArray.forEach((text: string, index: number): void => {
      const string = text.trim()
      if (index === 1) {
        bannerText = bannerText + `<span>${string}</span> `
      } else {
        bannerText = bannerText + string + ' '
      }
    })

    if (props.welcomeBannerText) {
      bannerText = `${props.welcomeBannerText.trim()} ` + bannerText
    }
  }

  let stepOneText = `Start Your Free Trial`
  if (!props.isTrial) {
    stepOneText = `Start Your Plan`
  }

  if (props.subscription_expires_at) {
    stepOneText = 'Renew Your Subscription'
  }

  let bannerBackground = 'unset'
  if (props.isPrideBanner) {
    bannerBackground = 'linear-gradient(90deg, #29187F 0%, #251231 25.35%, #960049 51.81%, #F0432D 77.99%, #FF883E 100%);'
  }

  return (
    <ContainerDiv>
      {!props.isInLine && <BackgroundGradient quadrant={4} /> }
      <GlobalStyle backgroundColor={colors.midnightBlue} />
      <HtmlHead
        title={'Dipsea | Subscribe'}
        description={
          'Get access to the entire Dipsea library and explore 1000+ spicy audiobooks, romance novels, sleep scenes, and steamy sounds. '
        }
        includeStripe={true}
        canonicalUrl={'https://www.dipseastories.com/subscribe'}
      />
      {!props.isInLine && (
        <SimpleNav
          backgroundColor={colors.midnightBlue90}
          buttonOrder={['why-dipsea', 'sign-out']}
        />
      )}

      {!!bannerText && (
        <TopBanner bannerText={bannerText} isPositionSticky={true} background={bannerBackground} />
      )}

      <Container>
        <Row>
          <Col
            xs={4}
            sm={8}
            md={6}
            lg={12}
            align={'center'}
            offset={{ md: 1, lg: 0 }}
          >
            <SubscribeFeaturesBlock>
              <Header dangerouslySetInnerHTML={{ __html: headerText }} />
              <Subheader>
                Explore hundreds of audio romance stories created for women, by women.
                <br/>Cancel anytime.
              </Subheader>
            </SubscribeFeaturesBlock>
          </Col>
          <Col
            xs={4}
            sm={8}
            md={6}
            lg={6}
            xl={5}
            offset={{ xl: 1, md: 1, lg: 0 }}
            align={{ sm: 'center', lg: 'flex-end' }}
          >
            <StepContainer>
              <StepText>STEP 1</StepText>

              <StepHeaderContainer>
                <StepHeader>
                  {stepOneText}
                </StepHeader>
              </StepHeaderContainer>

              <ChoosePlan />

              <PromoCode theme={'white'} />

              <SubscribeFeaturesList />

              <Availability />
            </StepContainer>
          </Col>

          <Col
            xs={4}
            sm={8}
            md={6}
            lg={6}
            xl={5}
            offset={{ md: 1, lg: 0 }}
            align={{ sm: 'center', lg: 'flex-start' }}
          >
            <StepContainer>
              {!props.loggedIn && (
                <CreateAccountDiv>
                  <StepText>STEP 2</StepText>

                  <AuthForm theme={'white'} />
                </CreateAccountDiv>
              )}
              {(props.loggedIn && (!props.isSubscribed || props.subscription_expires_at)) && (
                <CreateAccountDiv>
                  <StepText>STEP 3</StepText>
                  <StepHeaderContainer>
                    <H3 width='100%' color={colors.white}>
                      Enter billing information
                    </H3>
                  </StepHeaderContainer>
                  {stripePromise
                    ? (
                      <Elements stripe={stripePromise}>
                        <BillingInformation />
                      </Elements>
                      )
                    : (
                      <div style={{ padding: '20px', textAlign: 'center', color: colors.white }}>
                        <p>Payment processing is temporarily unavailable.</p>
                        <p>Please try again later or contact support.</p>
                      </div>
                      )}
                </CreateAccountDiv>
              )}
            </StepContainer>
          </Col>
        </Row>
      </Container>
    </ContainerDiv>
  )
}

const mapStateToProps = (state: AppStateType): PropsType => {
  const featurePride = state?.subscriptionPromo?.userEnteredPromo === 'pride2023' || state?.subscriptionPromo?.userEnteredPromo === 'PRIDE2023'
  let welcomeBannerText = state.subscriptionPromo?.welcomeBannerText || undefined
  let bannerArray = state.subscriptionText?.promoBannerText
  if (featurePride) {
    welcomeBannerText = undefined
    bannerArray = ['Happy Pride! Treat yourself to hundreds of queer sexy stories for 50% Off.', '', '']
  }

  return {
    loggedIn: !!(state.user || {}).id,
    isSubscribed: !!(state.user || {}).isSubscribed,
    subscription_expires_at: (state.user || {}).subscription_expires_at,
    isMonthly: (state.subscriptionText || {}).plan === 'monthly',
    headerArray: state.subscriptionText?.promoHeaderText || [
      'Get Unlimited Access',
      'to Dipsea'
    ],
    isTrial: !!(state.subscriptionText || {}).trial,
    bannerArray,
    welcomeBannerText,
    featureAnnualOnly: state.featureAnnualOnly,
    isPrideBanner: featurePride
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DipatchPropsType => {
  return {
    openSubscribeConfirmed: (): void => dispatch(openSubscribeConfirmed(true))
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SubscribeFlow1)
)

const SubscribeFeaturesList = (): React.Node => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  return (
    <>
      <Hidden xs={true}>
        <ValuePropText />
      </Hidden>
      <Hidden sm={true} md={true} lg={true} xl={true}>
        <MobileBenefitsBlock>
          <ViewSubscriberBenefitsBlock onClick={(): void => setIsOpen(!isOpen)}>
            <P
              fontSize={'14px'}
              lineHeight={'21px'}
              styles={'margin-right: 8px;'}
            >
              View subscriber benefits
            </P>
            <DownArrow size={15} color={colors.white} reverse={isOpen} />
          </ViewSubscriberBenefitsBlock>
          {isOpen && <ValuePropText />}
        </MobileBenefitsBlock>
      </Hidden>
    </>
  )
}

const ValuePropText = (): React.Node => {
  const valueProps = [
    'Hundreds of spicy audiobooks and romance novels',
    'New releases every week',
    'Your favorite tropes and irresistible new characters',
    'Diverse themes and sexy accents',
    'High-quality stories brought to life',
    'Cancel anytime'
  ]
  return (
    <>
      <ValuePropHeader>What subscribers get:</ValuePropHeader>

      <SubscribeFeaturesUL>
        {valueProps.map((text: string, index: number): React.Node => {
          return (
            <SubscribeFeaturesLI key={`value-prop-${index + 1}`}>
              <ValueProp
                isSimpleCheck
                text={text}
              />
            </SubscribeFeaturesLI>
          )
        })}
      </SubscribeFeaturesUL>
    </>
  )
}

const ValuePropHeader = styled.p`
  ${GoodSansB2};
  align-self: flex-start;
  margin: 16px 0 16px;
  font-size: 16px;
  ${(props: any): string => config(props).media.sm`
    margin: 0px 0 14px;
  `}
  ${(props: any): string => config(props).media.md`
    margin: 20px 0 16px;
  `}
   ${(props: any): string => config(props).media.lg`
    margin: 0px 0 16px;
    font-size: 20px;
  `}
`

const Availability = (): React.Node => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  return (
    <>
      <Hidden xs={true}>
        <StoreAvailability color={colors.white} />
      </Hidden>
      <Hidden sm={true} md={true} lg={true} xl={true}>
        <MobileBenefitsBlock>
          <ViewSubscriberBenefitsBlock onClick={(): void => setIsOpen(!isOpen)}>
            <P
              fontSize={'14px'}
              lineHeight={'21px'}
              styles={'margin-right: 8px;'}
            >
              View availability
            </P>
            <DownArrow size={15} color={colors.white} reverse={isOpen} />
          </ViewSubscriberBenefitsBlock>
          {isOpen && (
            <StoreAvailabilityBlock>
              <StoreAvailability color={colors.white} />
            </StoreAvailabilityBlock>
          )}
        </MobileBenefitsBlock>
      </Hidden>
    </>
  )
}

const ContainerDiv = styled.div`
  width: 100%;
  padding-bottom: 30px;
  position: relative;
`

const StoreAvailabilityBlock = styled.div`
  margin: 12px 0px;
`

const MobileBenefitsBlock = styled.div`
  border-bottom: 1px solid ${colors.white};
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const ViewSubscriberBenefitsBlock = styled.div`
  display: flex;
  width: 100%;

  align-items: center;

  ${(props: any): string => config(props).media.md`
       justify-content: center;
   `}
`

const SubscribeFeaturesBlock = styled.div`
  margin: 16px 0 0;
`

const Header = styled.h1`
  ${QueensH3};
  text-align: center;
  line-height: 120%;
  span {
    color: ${colors.tintOrange};
  }
  ${(props: any): string => config(props).media.lg`
    font-size: 60px;
    line-height: 110%;
    margin: 14px 0 0;
  `}
`
const Subheader = styled.p`
  ${GoodSansB1};
  line-height: 1.58rem;
  margin: 16px 0 26px;
  text-align: center;
  ${(props: any): string => config(props).media.sm`
    font-size: 18px;
    line-height: 1.9rem;
    max-width: 380px;
  `}
  ${(props: any): string => config(props).media.md`
    margin-bottom: 45px;
  `}
   ${(props: any): string => config(props).media.lg`
    max-width: 800px;
  `}
`

const SubscribeFeaturesUL = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-block: 0;
  width: 100%;

   ${(props: any): string => config(props).media.sm`
    margin: 0 0 10px;
  `}
`

const SubscribeFeaturesLI = styled.li`
  margin: 0px 0px 12px 0px;
  pading: 0;
  display: flex;
`
const CreateAccountDiv = styled.div`
  width: 100%;
`

const StepContainer = styled.div`
  border: 1px solid ${colors.white};
  border-radius: 10px;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  max-width: 424px;

  ${(props: any): string => config(props).media.md`
    align-items: start;
    padding: 40px 36px;
    width: 100%;
  `}
  ${(props: any): string => config(props).media.lg`
    width: unset;
  `}
`

const StepHeaderContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
  h3 {
    text-align: center;
    ${(props: any): string => config(props).media.lg`
      text-align: left;
    `}
  }
`

const StepText = styled.p`
  ${GoodSansB3};
  font-size: 12px;
  line-height: 18px;
  margin: 0 0 6px;
  text-align: center;
  width: 100%;
  ${(props: any): string => config(props).media.lg`
    font-size: 14px;
    text-align: left;
  `}
`
const StepHeader = styled(H3)`
  text-align: center;
  ${(props: any): string => config(props).media.lg`
      text-align: left;
   `}
`
